import { createRootRoute, createRoute, createRouter, redirect, type RouterHistory } from "@tanstack/react-router";
import { OwnerAnnounceScreen, OwnerInboxScreen, OwnerThreadScreen } from "@teamna/ui/owner/inbox.tsx";
import { OwnerGroupDetailScreen } from "@teamna/ui/owner/groups-detail.tsx";
import { OwnerGroupsScreen } from "@teamna/ui/owner/groups.tsx";
import { OwnerParentsScreen } from "@teamna/ui/owner/families/parents.tsx";
import { OwnerParticipantsScreen } from "@teamna/ui/owner/families/participants-screen.tsx";
import { OwnerParentScreen } from "@teamna/ui/owner/parent/screen.tsx";
import { OwnerPackagesScreen } from "@teamna/ui/owner/packages.tsx";
import { OwnerPaymentsScreen } from "@teamna/ui/owner/payments.tsx";
import { OwnerGetPaidScreen } from "@teamna/ui/owner/get-paid.tsx";
import { OwnerScheduleScreen } from "@teamna/ui/owner/schedule.tsx";
import { OwnerTakeAttendanceScreen } from "@teamna/ui/owner/take-attendance.tsx";
import { OwnerSettingsScreen } from "@teamna/ui/owner/settings.tsx";
import { OwnerTeamScreen } from "@teamna/ui/owner/team.tsx";
import { OwnerStaffClassesScreen } from "@teamna/ui/owner/staff-classes.tsx";
import { OwnerNewParticipantsScreen } from "@teamna/ui/owner/new-participants.tsx";
import { OwnerWhatsAppSyncScreen } from "@teamna/ui/owner/whatsapp-sync/screen.tsx";
import { SetupBalancesScreen } from "@teamna/ui/owner/setup/balances.tsx";
import { SetupBusinessScreen } from "@teamna/ui/owner/setup/business.tsx";
import { SetupImportCsvScreen } from "@teamna/ui/owner/setup/import-csv.tsx";
import { SetupImportScreen } from "@teamna/ui/owner/setup/import.tsx";
import { SetupInviteScreen } from "@teamna/ui/owner/setup/invite.tsx";
import { SetupPackagesScreen } from "@teamna/ui/owner/setup/packages.tsx";
import { SetupPlacementScreen } from "@teamna/ui/owner/setup/placement.tsx";
import { SetupSessionsScreen } from "@teamna/ui/owner/setup/sessions.tsx";
import { SetupTeamScreen } from "@teamna/ui/owner/setup/team.tsx";
import { ActAsAssistantScreen, ActAsHomeScreen } from "./routes/act-as-assistant.tsx";
import { ActAsLayout } from "./routes/act-as-layout.tsx";
import { AdminBusinessDetailScreen } from "./routes/business-detail.tsx";
import { AdminBusinessesScreen } from "./routes/businesses.tsx";
import { DemoClubsScreen } from "./routes/demos.tsx";
import { EarlyAccessScreen } from "./routes/early-access.tsx";
import { PlatformSettingsScreen } from "./routes/settings.tsx";
import { BillingFeedScreen } from "./routes/billing-feed.tsx";
import { CostsScreen } from "./routes/costs.tsx";
import { AdminHealthScreen } from "./routes/health.tsx";
import { AssistantReviewScreen } from "./routes/assistant-review.tsx";
import { AssistantChatReviewScreen } from "./routes/assistant-chat.tsx";
import { NotFoundScreen, RootLayout } from "./routes/root.tsx";
import { SignInScreen } from "./routes/sign-in.tsx";
import { StaffLayout } from "./routes/staff-layout.tsx";
import { SupportBoardScreen, SupportTicketScreen } from "./routes/support.tsx";
import { SupportListScreen } from "./routes/support-list.tsx";
import { SupportSettingsScreen } from "./routes/support-settings.tsx";
import { LeadsScreen } from "./routes/leads.tsx";
import { LeadLabelsScreen } from "./routes/leads-labels.tsx";
import { LeadRunsScreen } from "./routes/leads-runs.tsx";
import { LeadSettingsScreen } from "./routes/leads-settings.tsx";

/**
 * The Control Center route tree, mounted at `/` on cc.teamna.app.
 *
 *   /sign-in              one button, Google
 *   /businesses, /businesses/$id, /billing, /costs, /demos, /early-access, /health, /support, /support/list, /support/settings,
 *   /support/$ticketId, /leads, /leads/labels, /leads/runs, /leads/settings, /assistant,
 *   /assistant/$conversationId
 *                         the read views, in the staff shell
 *   /owner/*              acting as a business: the Desk owner screens from `@teamna/ui/owner`,
 *                         at the same addresses as in Desk, so their own links work unchanged.
 *                         `/owner/assistant` is the Control Center page (`act-as-assistant.tsx`):
 *                         the package and the suggestions, never a chat
 *
 * The staff session is an HttpOnly cookie the page cannot read, so the guard is the layouts'
 * `GET /v1/me`, not a `beforeLoad`.
 */

const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFoundScreen });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/businesses" });
  },
});

const signInRoute = createRoute({ getParentRoute: () => rootRoute, path: "/sign-in", component: SignInScreen });

// --- the read views -------------------------------------------------------------

const staffRoute = createRoute({ getParentRoute: () => rootRoute, id: "staff", component: StaffLayout });

const businessesRoute = createRoute({ getParentRoute: () => staffRoute, path: "/businesses", component: AdminBusinessesScreen });

const businessDetailRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/businesses/$id",
  component: AdminBusinessDetailScreen,
});

const healthRoute = createRoute({ getParentRoute: () => staffRoute, path: "/health", component: AdminHealthScreen });

const billingFeedRoute = createRoute({ getParentRoute: () => staffRoute, path: "/billing", component: BillingFeedScreen });

const costsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/costs", component: CostsScreen });

const demosRoute = createRoute({ getParentRoute: () => staffRoute, path: "/demos", component: DemoClubsScreen });
const earlyAccessRoute = createRoute({ getParentRoute: () => staffRoute, path: "/early-access", component: EarlyAccessScreen });

const settingsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/settings", component: PlatformSettingsScreen });

const supportRoute = createRoute({ getParentRoute: () => staffRoute, path: "/support", component: SupportBoardScreen });

const supportListRoute = createRoute({ getParentRoute: () => staffRoute, path: "/support/list", component: SupportListScreen });

const supportSettingsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/support/settings", component: SupportSettingsScreen });

const supportTicketRoute = createRoute({ getParentRoute: () => staffRoute, path: "/support/$ticketId", component: SupportTicketScreen });

// The Lead Finder. `?run=<id>` and `?lead=<id>` are the links the connector gives Claude.
const leadsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/leads", component: LeadsScreen });
const leadLabelsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/leads/labels", component: LeadLabelsScreen });
const leadRunsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/leads/runs", component: LeadRunsScreen });
const leadSettingsRoute = createRoute({ getParentRoute: () => staffRoute, path: "/leads/settings", component: LeadSettingsScreen });

// `?business=<id>` opens it with that business picked (the links from act-as, read by the screen).
const assistantReviewRoute = createRoute({ getParentRoute: () => staffRoute, path: "/assistant", component: AssistantReviewScreen });

const assistantChatRoute = createRoute({ getParentRoute: () => staffRoute, path: "/assistant/$conversationId", component: AssistantChatReviewScreen });

// --- acting as a business ------------------------------------------------------------

const ownerRoute = createRoute({ getParentRoute: () => rootRoute, path: "/owner", component: ActAsLayout });

// The same addresses as in Desk, so the owner screens' own links and `useParams({ from })` work.
// "announce" is declared before `$conversationId` so it is read as the word.
const ownerHomeRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/home", component: ActAsHomeScreen });
const ownerAssistantRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/assistant", component: ActAsAssistantScreen });
const ownerScheduleRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/schedule", component: OwnerScheduleScreen });
const ownerTakeAttendanceRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/schedule/$sessionId/attendance", component: OwnerTakeAttendanceScreen });
const ownerParentsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/parents", component: OwnerParentsScreen });
const ownerParentRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/parents/$id", component: OwnerParentScreen });
const ownerParticipantsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/participants", component: () => <OwnerParticipantsScreen view="list" /> });
const ownerParticipantsBoardRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/participants/board", component: () => <OwnerParticipantsScreen view="board" /> });
// The Families page, the members table and the profile became Parents (owner menu revamp, 2026-10-09).
const ownerFamiliesRedirect = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/families",
  beforeLoad: () => {
    throw redirect({ to: "/owner/parents", replace: true });
  },
});
const ownerMembersRedirect = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/members",
  beforeLoad: () => {
    throw redirect({ to: "/owner/parents", replace: true });
  },
});
const ownerMemberProfileRedirect = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/members/$id",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/owner/parents/$id", params: { id: params.id }, replace: true });
  },
});
const ownerInboxRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/inbox", component: OwnerInboxScreen });
const ownerAnnounceRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/inbox/announce", component: OwnerAnnounceScreen });
const ownerThreadRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/inbox/$conversationId", component: OwnerThreadScreen });
const ownerPackagesRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/packages", component: OwnerPackagesScreen });
const ownerGroupsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/groups", component: OwnerGroupsScreen });
const ownerGroupDetailRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/groups/$groupId", component: OwnerGroupDetailScreen });
// The timetable is a view of the Schedule page now (2026-10-10). Old links still land on it.
const ownerTimetableRoute = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/timetable",
  beforeLoad: () => {
    throw redirect({ to: "/owner/schedule", search: { view: "timetable" } });
  },
});
const ownerPaymentsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/payments", component: OwnerPaymentsScreen });
// Staff see the state of a business's payment account; the API refuses them the Stripe link.
const ownerGetPaidRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/get-paid", component: () => <OwnerGetPaidScreen readOnly /> });
const ownerTeamRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/team", component: () => <OwnerTeamScreen memberPage={false} /> });
const ownerStaffClassesRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/staff-classes", component: OwnerStaffClassesScreen });
// Names coaches typed, for the owner to link (coach-adds-swimmers). Act-as helps an owner link them.
const ownerNewParticipantsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/new-participants", component: OwnerNewParticipantsScreen });
// Act-as helps an owner sync a WhatsApp group (the API already lets the calls through). No Team photo here.
const ownerWhatsAppSyncRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/tools/whatsapp-sync", component: OwnerWhatsAppSyncScreen });
const ownerSettingsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/settings", component: () => <OwnerSettingsScreen planLink={false} /> });
const ownerSetupBusinessRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/business", component: SetupBusinessScreen });
const ownerSetupTeamRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/team", component: SetupTeamScreen });
const ownerSetupImportRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/import", component: SetupImportScreen });
const ownerSetupImportCsvRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/import/csv", component: SetupImportCsvScreen });
const ownerSetupSessionsRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/sessions", component: SetupSessionsScreen });
const ownerSetupPackagesRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/packages", component: SetupPackagesScreen });
const ownerSetupInviteRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/invite", component: SetupInviteScreen });
const ownerSetupPlacementRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/placement", component: SetupPlacementScreen });
const ownerSetupBalancesRoute = createRoute({ getParentRoute: () => ownerRoute, path: "/setup/balances", component: SetupBalancesScreen });

/** Owner Home links here when an account runs no business. Acting always has one; go back. */
const startBusinessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/start/business",
  beforeLoad: () => {
    throw redirect({ to: "/businesses" });
  },
});

const ownerIndexRoute = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/owner/home" });
  },
});

const ownerSetupIndexRoute = createRoute({
  getParentRoute: () => ownerRoute,
  path: "/setup",
  beforeLoad: () => {
    throw redirect({ to: "/owner/home", replace: true });
  },
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  startBusinessRoute,
  staffRoute.addChildren([
    businessesRoute,
    businessDetailRoute,
    billingFeedRoute,
    costsRoute,
    demosRoute,
    earlyAccessRoute,
    healthRoute,
    settingsRoute,
    supportRoute,
    supportListRoute,
    supportSettingsRoute,
    supportTicketRoute,
    leadsRoute,
    leadLabelsRoute,
    leadRunsRoute,
    leadSettingsRoute,
    assistantReviewRoute,
    assistantChatRoute,
  ]),
  ownerRoute.addChildren([
    ownerIndexRoute,
    ownerSetupIndexRoute,
    ownerHomeRoute,
    ownerAssistantRoute,
    ownerScheduleRoute,
    ownerTakeAttendanceRoute,
    ownerParentsRoute,
    ownerParentRoute,
    ownerParticipantsRoute,
    ownerParticipantsBoardRoute,
    ownerFamiliesRedirect,
    ownerMembersRedirect,
    ownerMemberProfileRedirect,
    ownerInboxRoute,
    ownerAnnounceRoute,
    ownerThreadRoute,
    ownerPackagesRoute,
    ownerGroupsRoute,
    ownerGroupDetailRoute,
    ownerTimetableRoute,
    ownerPaymentsRoute,
    ownerGetPaidRoute,
    ownerTeamRoute,
    ownerStaffClassesRoute,
    ownerNewParticipantsRoute,
    ownerWhatsAppSyncRoute,
    ownerSettingsRoute,
    ownerSetupBusinessRoute,
    ownerSetupTeamRoute,
    ownerSetupImportRoute,
    ownerSetupImportCsvRoute,
    ownerSetupSessionsRoute,
    ownerSetupPackagesRoute,
    ownerSetupInviteRoute,
    ownerSetupPlacementRoute,
    ownerSetupBalancesRoute,
  ]),
]);

export function createCcRouter(options?: { history?: RouterHistory }) {
  return createRouter({
    routeTree,
    defaultPreload: "intent",
    scrollRestoration: true,
    ...(options?.history ? { history: options.history } : {}),
  });
}

export type CcRouter = ReturnType<typeof createCcRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: CcRouter;
  }
}
